import React from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { useForm } from "react-hook-form";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SidePanel } from "@/components/ui/side-panel";
import { Textarea } from "@/components/ui/textarea";
import {
  ClientsKeys,
  createReferredClient,
} from "@/features/clients/api/clients";
import {
  getMentorLicensees,
  MentorLicenseesKeys,
} from "@/features/founders/api/mentor-founder";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  licenseeId: z.string().min(1, "Please select a founder"),
  leadName: z.string().min(2, "Lead name must be at least 2 characters"),
  leadEmail: z.string().email("Invalid email address"),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface ReferralSubmissionFormProps {
  children?: React.ReactNode;
}

const ReferralSubmissionForm = ({ children }: ReferralSubmissionFormProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      licenseeId: "",
      leadName: "",
      leadEmail: "",
      notes: "",
    },
  });

  // Fetch licensees
  const { data: licenseesData, isLoading: isLoadingLicensees } = useQuery({
    queryKey: [MentorLicenseesKeys.GET_LICENSEES],
    queryFn: () => getMentorLicensees(),
  });

  const createReferralMutation = useMutation({
    mutationFn: (data: FormValues) =>
      createReferredClient({
        licenseeId: data.licenseeId,
        name: data.leadName,
        customerEmail: data.leadEmail,
        notes: data.notes,
      }),
    onSuccess: () => {
      toast({
        title: "Lead referred successfully!",
        description: "The founder will be notified about the new lead.",
      });
      queryClient.invalidateQueries({
        queryKey: [ClientsKeys.ReferredClients],
      });
      queryClient.invalidateQueries({ queryKey: ["referral-stats"] });
      form.reset();
      setOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error referring lead",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: FormValues) => {
    createReferralMutation.mutate(values);
  };

  const trigger = children ? (
    <span onClick={() => setOpen(true)} className="inline-flex">
      {children}
    </span>
  ) : (
    <Button className="flex items-center gap-2" onClick={() => setOpen(true)}>
      <UserPlus className="h-4 w-4" />
      Refer Lead
    </Button>
  );

  const footer = (
    <div className="flex justify-end gap-3">
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen(false)}
        disabled={createReferralMutation.isPending}
      >
        Cancel
      </Button>
      <Button
        type="button"
        onClick={form.handleSubmit(onSubmit)}
        disabled={createReferralMutation.isPending || !form.formState.isValid}
      >
        {createReferralMutation.isPending ? "Referring Lead..." : "Refer Lead"}
      </Button>
    </div>
  );

  return (
    <>
      {trigger}
      <SidePanel
        open={open}
        onOpenChange={setOpen}
        title="Add New Lead"
        subtitle="Refer a lead to one of your founders and earn 25% revenue share on successful deals."
        footer={footer}
      >
        <Form {...form}>
          <form className="space-y-6">
            <FormField
              control={form.control}
              name="licenseeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Assign to Founder</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    disabled={isLoadingLicensees}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a founder" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingLicensees ? (
                        <SelectItem value="loading" disabled>
                          Loading founders...
                        </SelectItem>
                      ) : licenseesData?.items?.length === 0 ? (
                        <SelectItem value="empty" disabled>
                          No founders found
                        </SelectItem>
                      ) : (
                        licenseesData?.items?.map((licensee) => (
                          <SelectItem key={licensee._id} value={licensee._id}>
                            {licensee.firstName} {licensee.lastName}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="leadName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lead Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="leadEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Lead Email Address</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="john.doe@company.com"
                      type="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes or Context (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any relevant context about the lead, their needs, budget, timeline, etc."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </SidePanel>
    </>
  );
};

export default ReferralSubmissionForm;
